import React from 'react';
import { Eye, EyeOff, GripVertical, Plus, Trash2 } from 'lucide-react';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FeatureLimitHint } from '../../../components/FeatureLimitHint';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { useFeatureLimits } from '../../../hooks/useFeatureLimits';
import {
  EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY,
  fallbackEmployeeDocumentTypes,
  parseEmployeeDocumentTypeRows,
  type EmployeeDocumentTypeRow,
} from '../../../utils/employeeDocumentTypes';
import { settingsValuesEqual } from '../utils/settingsDirty';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_INPUT_CLASS,
} from '../utils/settingsSectionStyles';

function newCustomRow(sortOrder: number): EmployeeDocumentTypeRow {
  return {
    key: `custom_${Date.now()}_${sortOrder}`,
    label: '',
    sort_order: sortOrder,
    is_enabled: true,
    is_system: false,
  };
}

function SortableDocumentTypeRow({
  row,
  onUpdate,
  onRemove,
}: {
  row: EmployeeDocumentTypeRow;
  onUpdate: (patch: Partial<EmployeeDocumentTypeRow>) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.key });
  const canDisable = row.key !== EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY;
  const canRemove = !row.is_system && row.key !== EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
    opacity: isDragging ? 0.92 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex flex-wrap items-center gap-2.5 py-1.5 border-b border-app-border last:border-0 bg-app-surface"
    >
      <button
        type="button"
        className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-app-text-muted hover:bg-app-hover cursor-grab active:cursor-grabbing touch-none"
        title="Ziehen zum Sortieren"
        aria-label="Reihenfolge per Ziehen ändern"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" strokeWidth={2} />
      </button>
      <input
        type="text"
        value={row.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        className={`flex-1 min-w-[10rem] ${SETTINGS_INPUT_CLASS}`}
        placeholder="Bezeichnung"
      />
      <button
        type="button"
        onClick={() => canDisable && onUpdate({ is_enabled: !row.is_enabled })}
        disabled={!canDisable}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover disabled:opacity-40 disabled:hover:bg-transparent"
        title={
          !canDisable
            ? '„Sonstiges“ bleibt immer sichtbar'
            : row.is_enabled
              ? 'Ausblenden'
              : 'Einblenden'
        }
        aria-label={row.is_enabled ? 'Sichtbar' : 'Ausgeblendet'}
        aria-pressed={row.is_enabled}
      >
        {row.is_enabled ? (
          <Eye className="w-4 h-4" strokeWidth={1.75} />
        ) : (
          <EyeOff className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
        )}
      </button>
      <button
        type="button"
        onClick={onRemove}
        disabled={!canRemove}
        className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 shrink-0 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
        title={canRemove ? 'Typ entfernen' : 'Standardtyp kann nicht entfernt werden'}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}

export function EmployeeDocumentTypesSettingsSection() {
  const [rows, setRows] = React.useState<EmployeeDocumentTypeRow[]>([]);
  const [baseline, setBaseline] = React.useState<EmployeeDocumentTypeRow[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [catalogReady, setCatalogReady] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [typeDraft, setTypeDraft] = React.useState('');
  const { usage, loaded: limitsReady } = useFeatureLimits();
  const docTypeLimit = usage('employeeDocumentTypes');
  const customDocTypeCount = rows.filter((row) => !row.is_system).length;
  const docTypesAtLimit = customDocTypeCount >= docTypeLimit.max;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const applyRows = React.useCallback((next: EmployeeDocumentTypeRow[]) => {
    const normalized = next.map((row, idx) => ({ ...row, sort_order: idx }));
    setRows(normalized);
    setBaseline(normalized);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/settings/employee-document-types', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Laden fehlgeschlagen'))))
      .then((data) => {
        if (cancelled) return;
        const parsed = parseEmployeeDocumentTypeRows(data);
        if (parsed.length === 0) throw new Error('Leerer Katalog');
        applyRows(parsed);
        setCatalogReady(true);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setRows(fallbackEmployeeDocumentTypes());
        setCatalogReady(false);
        setError('Dokumenttypen konnten nicht geladen werden. Speichern ist deaktiviert, damit keine Typen überschrieben werden.');
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [applyRows]);

  const rowsForSave = React.useMemo(
    () =>
      rows.filter((row) => row.is_system || row.key === EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY || row.label.trim().length > 0),
    [rows],
  );

  const isDirty = catalogReady && rows.length > 0 && !settingsValuesEqual(rowsForSave, baseline);

  const save = async () => {
    if (!catalogReady) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/settings/employee-document-types', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          rowsForSave.map((row, idx) => ({
            key: row.key,
            label: row.label.trim(),
            sort_order: idx,
            is_enabled: row.is_enabled,
          })),
        ),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError((data as { error?: string } | null)?.error || 'Speichern fehlgeschlagen.');
        return;
      }
      const parsed = parseEmployeeDocumentTypeRows(data);
      applyRows(parsed.length > 0 ? parsed : rowsForSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError('Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const update = (key: string, patch: Partial<EmployeeDocumentTypeRow>) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const next = { ...row, ...patch };
        if (row.key === EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY) next.is_enabled = true;
        return next;
      }),
    );
    setError(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows((prev) => {
      const from = prev.findIndex((row) => row.key === String(active.id));
      const to = prev.findIndex((row) => row.key === String(over.id));
      if (from < 0 || to < 0) return prev;
      return arrayMove(prev, from, to).map((item, idx) => ({ ...item, sort_order: idx }));
    });
    setError(null);
  };

  const add = () => {
    const label = typeDraft.trim();
    if (!label || !catalogReady || customDocTypeCount >= docTypeLimit.max) return;
    const next = { ...newCustomRow(rows.length), label };
    setRows((prev) => [...prev, next]);
    setTypeDraft('');
    setError(null);
  };

  const remove = (key: string) => {
    setRows((prev) => {
      const row = prev.find((item) => item.key === key);
      if (!row || row.is_system || row.key === EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY) return prev;
      return prev.filter((item) => item.key !== key).map((item, idx) => ({ ...item, sort_order: idx }));
    });
    setError(null);
  };

  return (
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      <SettingsCard
        title="Mitarbeiter-Dokumente"
        description="Diese Typen erscheinen als Filter und beim Hochladen auf der Mitarbeiter-Seite. Ausgeblendete Typen bleiben für vorhandene Dateien erhalten. Entfernte eigene Typen werden beim Speichern nach „Sonstiges“ verschoben."
      >
        {!loaded ? (
          <InlineLoadingState label="Dokumenttypen werden geladen …" className="justify-start" />
        ) : (
          <div className="space-y-4">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={rows.map((row) => row.key)} strategy={verticalListSortingStrategy}>
                <ul className="space-y-0">
                  {rows.map((row) => (
                    <SortableDocumentTypeRow
                      key={row.key}
                      row={row}
                      onUpdate={(patch) => update(row.key, patch)}
                      onRemove={() => remove(row.key)}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <div className="flex min-w-[12rem] flex-1 items-center gap-2">
                <input
                  type="text"
                  value={typeDraft}
                  onChange={(e) => setTypeDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    add();
                  }}
                  maxLength={120}
                  placeholder="Neuer Typ"
                  className={`min-w-0 flex-1 ${SETTINGS_INPUT_CLASS}`}
                  disabled={!catalogReady || docTypesAtLimit}
                />
                <button
                  type="button"
                  onClick={add}
                  disabled={!catalogReady || docTypesAtLimit || !typeDraft.trim()}
                  className={`${SETTINGS_BTN_OUTLINE} border-dashed border-pipedrive-blue text-pipedrive-blue hover:bg-blue-50 disabled:opacity-40`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Hinzufügen
                </button>
                <FeatureLimitHint
                  limitKey="employeeDocumentTypes"
                  used={customDocTypeCount}
                  max={docTypeLimit.max}
                  ready={limitsReady}
                />
              </div>
              <button
                type="button"
                onClick={() => void save()}
                disabled={!isDirty || saving}
                className={
                  isDirty
                    ? SETTINGS_BTN_PRIMARY
                    : 'inline-flex h-9 shrink-0 items-center px-3.5 text-sm font-medium rounded-md text-app-text-subtle bg-app-surface-muted disabled:opacity-40'
                }
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
              <SettingsCardSavedStatus saved={saved} />
              {error ? <span className="text-sm text-red-600">{error}</span> : null}
            </div>
          </div>
        )}
      </SettingsCard>
    </div>
  );
}
